import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputAdornment } from '@mui/material';
import { CalculationsSummaryAircraftProps } from '../types/ESALTypesAircraft';
import { ESALFormulas } from '../ESALFormulas';
import DecimalTextField from '../../../../components/DecimalTextField';
import { resolveDecimalFieldPolicy } from '../../../../utils/decimalInput';

/**
 * Excel-format traffic analysis summary component for Aircraft ESAL modal
 * Matches the exact structure from TRAFFIC LOAD.txt (H84-H91) with 4 editable inputs and 4 calculated outputs
 * Now uses Redux state directly without local state duplication
 */
const CalculationsSummaryAircraft: React.FC<CalculationsSummaryAircraftProps> = ({ summaryData, totalESAL, isCalculating, onSummaryInputsChange, onValidityChange, resetKey }) => {
    const { t } = useTranslation();

    // Use summaryData directly from Redux (no local state) - memoized to prevent re-renders
    const editableInputs = useMemo(
        () => ({
            dd: summaryData.directionalDistribution,
            dl: summaryData.laneDistribution,
            analysisPeriod: summaryData.analysisPeriod,
            growthRate: summaryData.yearGrowthRate,
        }),
        [summaryData.directionalDistribution, summaryData.laneDistribution, summaryData.analysisPeriod, summaryData.yearGrowthRate]
    );

    // Calculate derived values based on inputs (Excel-exact formulas from H112-H119 and J112-J119)
    const calculatedValues = useMemo(() => {
        // H112/J112 = Q110*365 (N1 = Total aircraft ESAL per year)
        const totalPassagesPerYear = totalESAL * 365; // Keep full precision for calculations

        // AASHTO Method (H column)
        // H117 = TGF using standard AASHTO formula: ((1+r)^Y - 1) / ln(1+r)
        const aashtoTgf = ESALFormulas.calculateTrafficGrowthFactor(editableInputs.growthRate, editableInputs.analysisPeriod);

        // H118 = w'18 = TGF * N1 (standard traffic growth calculation)
        const aashtoTotalVolumeTraffic = aashtoTgf * totalPassagesPerYear;

        // H119 = w18 = DD * DL * w'18 (traffic in design lane)
        const aashtoTrafficDesignLane = editableInputs.dd * editableInputs.dl * aashtoTotalVolumeTraffic;

        // FAA Method (J column)
        // J118 = FAA simplified formula: (1 + (r * Y / 200)) * N1 * Y
        const faaEsalCalculation = ESALFormulas.calculateFAAProjectedESAL(totalESAL, editableInputs.analysisPeriod, editableInputs.growthRate);

        // J119 = J118 / 56.69 (aircraft total departures)
        const aircraftTotalDepartures = faaEsalCalculation / 56.69;

        return {
            totalPassagesPerYear,
            aashtoTgf,
            aashtoTotalVolumeTraffic,
            aashtoTrafficDesignLane,
            faaEsalCalculation,
            aircraftTotalDepartures,
        };
    }, [editableInputs, totalESAL]);

    /**
     * Handle input field changes - dispatch directly to Redux
     */
    const handleInputChange = useCallback(
        (field: keyof typeof editableInputs, value: number) => {
            if (onSummaryInputsChange) {
                const updatedInputs = {
                    dd: editableInputs.dd,
                    dl: editableInputs.dl,
                    analysisPeriod: editableInputs.analysisPeriod,
                    growthRate: editableInputs.growthRate,
                    trafficDesignLane: calculatedValues.faaEsalCalculation,
                    [field]: value,
                };
                onSummaryInputsChange(updatedInputs);
            }
        },
        [editableInputs, calculatedValues.faaEsalCalculation, onSummaryInputsChange]
    );

    /**
     * Render editable input field
     */
    const renderEditableField = (field: keyof typeof editableInputs, label: string, unit: string, decimals = 2) => (
        <TableRow>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', width: '60%' }}>{label}</TableCell>
            <TableCell sx={{ textAlign: 'right', minWidth: 120, width: '40%' }}>
                <DecimalTextField
                    size="small"
                    value={editableInputs[field]}
                    policy={resolveDecimalFieldPolicy({ decimalPlaces: decimals }, 'esalSummary')}
                    emptyPolicy="publishZero"
                    fieldLabel={label}
                    onValueChange={value => handleInputChange(field, value ?? 0)}
                    onValidityChange={isValid => onValidityChange?.(`summary:${field}`, isValid)}
                    resetKey={resetKey}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ width: '100%' }}
                    InputProps={{
                        endAdornment: unit && <InputAdornment position="end">{unit}</InputAdornment>,
                    }}
                />
            </TableCell>
        </TableRow>
    );

    /**
     * Render calculated field (read-only)
     */
    const renderCalculatedField = (label: string, value: number, unit: string, decimals = 2) => (
        <TableRow>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', width: '60%' }}>{label}</TableCell>
            <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', width: '40%' }}>
                {ESALFormulas.formatNumber(value, decimals)} {unit}
            </TableCell>
        </TableRow>
    );

    return (
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Loading indicator */}
            {isCalculating && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                        {t('esal_calculating', 'Calculating...')}
                    </Typography>
                </Box>
            )}

            {/* Excel-format summary table - exact match to H84-H91 */}
            <Box sx={{ mb: 3, flex: 1 }}>
                <TableContainer component={Paper} sx={{ maxHeight: '400px' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', width: '60%' }}>{t('esal_table_description', 'Description')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', textAlign: 'center', width: '40%' }}>{t('esal_table_value', 'Value')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* INPUT FIELDS (4 editable - matching Excel H85-H88) */}
                            {renderEditableField('dd', t('esal_summary_directional_distribution', 'Directional distribution factor'), '', 2)}
                            {renderEditableField('dl', t('esal_summary_lane_distribution', 'Lane distribution factor'), '', 2)}
                            {renderEditableField('analysisPeriod', t('esal_summary_analysis_period', 'Analysis period'), '', 0)}
                            {renderEditableField('growthRate', t('esal_summary_growth_rate', 'Year growth rate'), '%', 2)}

                            {/* CALCULATED FIELDS - matching Excel display */}
                            {renderCalculatedField(
                                t('esal_summary_total_movements_per_year', 'Total number of passages N1'),
                                calculatedValues.totalPassagesPerYear,
                                t('esal_unit_movements_per_year', 'movements/year'),
                                0
                            )}
                            {renderCalculatedField('Aircraft total departures (FAA)', calculatedValues.aircraftTotalDepartures, 'departures', 0)}
                            {renderCalculatedField('ESAL CALCULATION (FAA)', calculatedValues.faaEsalCalculation, 'passages ESAL 80kN', 0)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default CalculationsSummaryAircraft;
